import {
  ERROR_MESSAGES,
  LABS_VECTOR_PRODUCT_TYPES,
  VECTOR_PRODUCT_OPTIONS,
  VECTOR_PRODUCT_SIZE,
} from '../../S/Constant/composite.js';

/**
 * 메이플 캐릭터 벡터 상품 생성을 위한 입력값을 검증합니다.
 * @param {Object} params - 검증할 파라미터 객체
 * @param {string} params.image_url - 이미지 URL
 * @param {string} params.productType - 상품 타입. LABS_VECTOR_PRODUCT_TYPES의 값 중 하나
 * @param {string} params.size - 상품 크기. VECTOR_PRODUCT_SIZE[productType]의 값 중 하나
 * @throws {Error} 유효하지 않은 입력값이 있을 경우
 */
export function validateMakeMapleCharacterVectorProductInput({ image_url, productType, size }) {
  // 1. image_url 검증
  if (!image_url || typeof image_url !== 'string') {
    throw new Error(ERROR_MESSAGES.IMAGE_URL_REQUIRED);
  }

  try {
    new URL(image_url);
  } catch {
    throw new Error(ERROR_MESSAGES.INVALID_IMAGE_URL);
  }

  // 2. productType 검증
  if (!Object.keys(VECTOR_PRODUCT_OPTIONS).includes(productType)) {
    throw new Error(ERROR_MESSAGES.INVALID_PRODUCT_TYPE);
  }

  // 3. size 검증
  const validSizes = Object.values(VECTOR_PRODUCT_SIZE[productType]);
  if (!validSizes.includes(size)) {
    throw new Error(ERROR_MESSAGES.INVALID_PRODUCT_SIZE);
  }
}

export function validateOptionsByProductType({ productType, options }) {
  if (!options || typeof options !== 'object') {
    throw new Error(ERROR_MESSAGES.INVALID_CUTLINE_OPTIONS);
  }

  switch (productType) {
    case LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND:
      validateAcrylicStandOptions(options);
      break;
    case LABS_VECTOR_PRODUCT_TYPES.KEYRING:
      validateKeyringOptions(options);
      break;
  }
}

function validateAcrylicStandOptions(options) {
  // 1. 기본 필수 필드 검증
  validateCommonOptions(options);

  // 2. 아크릴 스탠드 전용 필드 검증
  const { STAND_SIZE_MM } = options;
  if (!STAND_SIZE_MM || typeof STAND_SIZE_MM !== 'object') {
    throw new Error(ERROR_MESSAGES.STAND_SIZE_REQUIRED);
  }

  const { GROUND, NOTCH } = STAND_SIZE_MM;

  // Validate ground size
  if (!GROUND || typeof GROUND !== 'object') {
    throw new Error(ERROR_MESSAGES.GROUND_SIZE_REQUIRED);
  }

  const { WIDTH: groundWidth, HEIGHT: groundHeight } = GROUND;
  if (typeof groundWidth !== 'number' || groundWidth <= 0) {
    throw new Error(ERROR_MESSAGES.INVALID_GROUND_WIDTH);
  }
  if (typeof groundHeight !== 'number' || groundHeight < 0) {
    throw new Error(ERROR_MESSAGES.INVALID_GROUND_HEIGHT);
  }

  // Validate notch size
  if (!NOTCH || typeof NOTCH !== 'object') {
    throw new Error(ERROR_MESSAGES.NOTCH_SIZE_REQUIRED);
  }

  const { WIDTH: notchWidth, HEIGHT: notchHeight, ROUND } = NOTCH;
  if (typeof notchWidth !== 'number' || notchWidth <= 0) {
    throw new Error(ERROR_MESSAGES.INVALID_NOTCH_WIDTH);
  }
  if (typeof notchHeight !== 'number' || notchHeight < 1) {
    // Changed to enforce minimum height of 1
    throw new Error(ERROR_MESSAGES.INVALID_NOTCH_HEIGHT);
  }
  if (typeof ROUND !== 'number' || ROUND <= 0) {
    throw new Error(ERROR_MESSAGES.INVALID_NOTCH_ROUND);
  }

  // Validate ground width is larger than notch width
  if (groundWidth <= notchWidth) {
    throw new Error('Ground width must be greater than notch width');
  }
}

function validateKeyringOptions(options) {
  // 1. 기본 필수 필드 검증
  validateCommonOptions(options);

  // 2. 키링 전용 필드 검증
  const { RING_SIZE_MM } = options;
  if (!RING_SIZE_MM || typeof RING_SIZE_MM !== 'object') {
    throw new Error(ERROR_MESSAGES.RING_SIZE_REQUIRED);
  }

  const { RADIUS } = RING_SIZE_MM;
  if (!RADIUS || typeof RADIUS !== 'object') {
    throw new Error(ERROR_MESSAGES.RING_RADIUS_REQUIRED);
  }

  const { INNER: innerRadius, OUTER: outerRadius } = RADIUS;
  if (typeof innerRadius !== 'number' || innerRadius <= 0) {
    throw new Error(ERROR_MESSAGES.INVALID_INNER_RADIUS);
  }
  if (typeof outerRadius !== 'number' || outerRadius <= 0) {
    throw new Error(ERROR_MESSAGES.INVALID_OUTER_RADIUS);
  }
  if (outerRadius - innerRadius < 1) {
    throw new Error(ERROR_MESSAGES.INVALID_RADIUS_RELATION);
  }
}

/**
 * VECTOR_PRODUCT_OPTIONS의 공통 필드를 검증합니다.
 * @param {Object} options - 검증할 옵션 객체
 * @throws {Error} 유효하지 않은 값이 있을 경우
 */
function validateCommonOptions(options) {
  // 1. NAME 검증
  if (!options.NAME || typeof options.NAME !== 'string') {
    throw new Error(ERROR_MESSAGES.NAME_REQUIRED);
  }

  // 2. TARGET_SCALE 검증
  if (typeof options.TARGET_SCALE !== 'number' || options.TARGET_SCALE <= 0) {
    throw new Error(ERROR_MESSAGES.INVALID_PRODUCT_TARGET_SCALE);
  }

  // 3. MAX_SIZE_MM 검증
  if (!options.MAX_SIZE_MM || typeof options.MAX_SIZE_MM !== 'object') {
    throw new Error(ERROR_MESSAGES.MAX_SIZE_REQUIRED);
  }
  const { WIDTH, HEIGHT } = options.MAX_SIZE_MM;
  if (typeof WIDTH !== 'number' || WIDTH <= 0) {
    throw new Error(ERROR_MESSAGES.MAX_SIZE_WIDTH_INVALID);
  }
  if (typeof HEIGHT !== 'number' || HEIGHT <= 0) {
    throw new Error(ERROR_MESSAGES.MAX_SIZE_HEIGHT_INVALID);
  }

  // 4. CUTLINE_YOYO 검증
  if (typeof options.CUTLINE_YOYO !== 'number' || options.CUTLINE_YOYO < 0) {
    throw new Error(ERROR_MESSAGES.INVALID_CUTLINE_YOYO);
  }

  // 5. BINARIZE_ALPHA_THRESHOLD 검증
  if (
    typeof options.BINARIZE_ALPHA_THRESHOLD !== 'number' ||
    options.BINARIZE_ALPHA_THRESHOLD < 0 ||
    options.BINARIZE_ALPHA_THRESHOLD > 1
  ) {
    throw new Error(ERROR_MESSAGES.INVALID_ALPHA_THRESHOLD);
  }

  // 6. DPI 검증
  if (!Number.isInteger(options.DPI) || options.DPI <= 0) {
    throw new Error(ERROR_MESSAGES.INVALID_DPI);
  }

  // 7. CUTLINE_MM 검증
  if (!options.CUTLINE_MM || typeof options.CUTLINE_MM !== 'object') {
    throw new Error(ERROR_MESSAGES.INVALID_CUTLINE_OPTIONS);
  }
  const { OFFSET, SMOOTHNESS } = options.CUTLINE_MM;
  if (typeof OFFSET !== 'number' || OFFSET < 0) {
    throw new Error(ERROR_MESSAGES.INVALID_CUTLINE_OFFSET);
  }
  if (typeof SMOOTHNESS !== 'number' || SMOOTHNESS < 0) {
    throw new Error(ERROR_MESSAGES.INVALID_CUTLINE_SMOOTHNESS);
  }
}

export function validatePaddingOption(padding_options) {
  if (!padding_options || typeof padding_options !== 'object') {
    throw new Error(ERROR_MESSAGES.INVALID_PADDING_OPTIONS);
  }

  const { TOP, BOTTOM, RIGHT, LEFT, ...rest } = padding_options;

  // Check for unexpected padding keys
  if (Object.keys(rest).length > 0) {
    throw new Error(ERROR_MESSAGES.INVALID_PADDING_KEYS);
  }

  if (TOP !== undefined && (typeof TOP !== 'number' || TOP < 0)) {
    throw new Error(ERROR_MESSAGES.INVALID_TOP_PADDING);
  }

  if (BOTTOM !== undefined && (typeof BOTTOM !== 'number' || BOTTOM < 0)) {
    throw new Error(ERROR_MESSAGES.INVALID_BOTTOM_PADDING);
  }

  if (RIGHT !== undefined && (typeof RIGHT !== 'number' || RIGHT < 0)) {
    throw new Error(ERROR_MESSAGES.INVALID_RIGHT_PADDING);
  }

  if (LEFT !== undefined && (typeof LEFT !== 'number' || LEFT < 0)) {
    throw new Error(ERROR_MESSAGES.INVALID_LEFT_PADDING);
  }
}

/**
 * 참 이미지 생성을 위한 입력값을 검증합니다.
 * @param {Object} params - 검증할 파라미터 객체
 * @param {Object} params.backgroundImageUrls - 배경 이미지 URL 객체
 * @param {string} params.backgroundImageUrls.left - 왼쪽 배경 이미지 URL
 * @param {string} params.backgroundImageUrls.mid - 중앙 배경 이미지 URL
 * @param {string} params.backgroundImageUrls.right - 오른쪽 배경 이미지 URL
 * @param {string} params.fontUrl - 폰트 파일 URL
 * @param {string} params.padding - CSS 스타일 패딩 값
 * @param {string} params.text - 렌더링할 텍스트
 * @param {Object} params.textProps - 텍스트 스타일 속성
 * @param {string} params.textProps.fontColor - 폰트 색상
 * @param {number} params.textProps.fontSize - 폰트 크기
 * @param {string} params.textProps.fontUrl - 폰트 URL
 * @throws {Error} 유효하지 않은 입력값이 있을 경우
 */
export function validateMakeCharmImageInput({ backgroundImageUrls, fontUrl, padding, text, textProps }) {
  validateBackgroundImageUrls(backgroundImageUrls);
  validateUrls({ fontUrl });
  validatePaddingString(padding);
  validateText(text);
  validateTextProps(textProps);
}

/**
 * 배경 이미지 URL들을 검증합니다.
 * @private
 * @param {Object} backgroundImageUrls - 배경 이미지 URL 객체
 * @throws {Error} 유효하지 않은 URL이 있을 경우
 */
function validateBackgroundImageUrls(backgroundImageUrls) {
  if (!backgroundImageUrls || typeof backgroundImageUrls !== 'object') {
    throw new Error(ERROR_MESSAGES.INVALID_BACKGROUND_URLS);
  }

  const { left, mid, right } = backgroundImageUrls;

  if (!left || typeof left !== 'string') {
    throw new Error(ERROR_MESSAGES.INVALID_LEFT_BACKGROUND_URL);
  }

  if (!mid || typeof mid !== 'string') {
    throw new Error(ERROR_MESSAGES.INVALID_MID_BACKGROUND_URL);
  }

  if (!right || typeof right !== 'string') {
    throw new Error(ERROR_MESSAGES.INVALID_RIGHT_BACKGROUND_URL);
  }

  // URL 형식 검증
  [left, mid, right].forEach((url) => {
    try {
      new URL(url);
    } catch {
      throw new Error(ERROR_MESSAGES.INVALID_URL_FORMAT);
    }
  });
}

/**
 * URL들을 검증합니다.
 * @private
 * @param {Object} urls - URL 객체
 * @param {string} urls.fontUrl - 폰트 파일 URL
 * @throws {Error} 유효하지 않은 URL이 있을 경우
 */
function validateUrls({ fontUrl }) {
  if (!fontUrl || typeof fontUrl !== 'string') {
    throw new Error(ERROR_MESSAGES.INVALID_FONT_URL);
  }

  try {
    new URL(fontUrl);
  } catch {
    throw new Error(ERROR_MESSAGES.INVALID_URL_FORMAT);
  }
}

/**
 * 패딩 문자열을 검증합니다.
 * @private
 * @param {string} padding - CSS 스타일 패딩 값
 * @throws {Error} 유효하지 않은 패딩 값일 경우
 */
function validatePaddingString(padding) {
  if (!padding || typeof padding !== 'string') {
    throw new Error(ERROR_MESSAGES.INVALID_PADDING_STRING);
  }

  const values = padding.split(' ').map(Number);

  if (values.length !== 1 && values.length !== 2) {
    throw new Error(ERROR_MESSAGES.INVALID_PADDING_STRING);
  }

  if (values.some(isNaN) || values.some((v) => v < 0)) {
    throw new Error(ERROR_MESSAGES.INVALID_PADDING_STRING);
  }
}

/**
 * 텍스트를 검증합니다.
 * @private
 * @param {string} text - 렌더링할 텍스트
 * @throws {Error} 유효하지 않은 텍스트일 경우
 */
function validateText(text) {
  if (!text || typeof text !== 'string' || text.length === 0) {
    throw new Error(ERROR_MESSAGES.INVALID_TEXT);
  }
}

/**
 * 텍스트 스타일 속성을 검증합니다.
 * @private
 * @param {Object} textProps - 텍스트 스타일 속성
 * @throws {Error} 유효하지 않은 텍스트 스타일 속성이 있을 경우
 */
function validateTextProps(textProps) {
  if (!textProps || typeof textProps !== 'object') {
    throw new Error(ERROR_MESSAGES.INVALID_TEXT_PROPS);
  }

  const { fontColor, fontSize, fontUrl } = textProps;

  if (!fontColor || typeof fontColor !== 'string') {
    throw new Error(ERROR_MESSAGES.INVALID_FONT_COLOR);
  }

  if (!fontSize || typeof fontSize !== 'number' || fontSize <= 0) {
    throw new Error(ERROR_MESSAGES.INVALID_FONT_SIZE);
  }

  if (!fontUrl || typeof fontUrl !== 'string') {
    throw new Error(ERROR_MESSAGES.INVALID_FONT_URL);
  }

  try {
    new URL(fontUrl);
  } catch {
    throw new Error(ERROR_MESSAGES.INVALID_URL_FORMAT);
  }
}

/**
 * 키링 참 생성을 위한 입력값을 검증합니다.
 * @param {Object} params - 검증할 파라미터 객체
 * @param {string} params.text - 렌더링할 텍스트
 * @param {string} params.fontColor - 폰트 색상 (CSS 색상 값)
 * @param {number} params.fontSize - 폰트 크기 (px)
 * @param {number} params.tradeMarkgapPx - 텍스트와 상표 이미지 사이의 간격 (px)
 * @param {string} params.padding - CSS 스타일 패딩 값 (예: "20 0")
 * @param {string} params.tradeMarkImageUrl - 상표 이미지 URL
 * @param {Object} params.cutlineMargin - 커팅 라인 마진 설정
 * @param {number} params.cutlineRectRound - 커팅 라인 모서리 라운드 값 (px)
 * @param {number} params.ringConnectionRoundPx - 링 연결부 라운드 값 (px)
 * @param {Object} params.backgroundImageUrls - 배경 이미지 URL
 * @throws {Error} 유효하지 않은 입력값이 있을 경우
 */
export function validateMakeKeyringCharmInput({
  text,
  fontColor,
  fontSize,
  tradeMarkgapPx,
  padding,
  tradeMarkImageUrl,
  cutlineMargin,
  cutlineRectRound,
  ringConnectionRoundPx,
  backgroundImageUrls,
}) {
  validateText(text);

  if (!fontColor || typeof fontColor !== 'string') {
    throw new Error(ERROR_MESSAGES.INVALID_FONT_COLOR);
  }

  if (!fontSize || typeof fontSize !== 'number' || fontSize <= 0) {
    throw new Error(ERROR_MESSAGES.INVALID_FONT_SIZE);
  }

  if (typeof tradeMarkgapPx !== 'number' || tradeMarkgapPx < 0) {
    throw new Error(ERROR_MESSAGES.INVALID_TRADEMARK_GAP);
  }

  validatePaddingString(padding);

  if (!tradeMarkImageUrl || typeof tradeMarkImageUrl !== 'string') {
    throw new Error(ERROR_MESSAGES.INVALID_TRADEMARK_URL);
  }

  try {
    new URL(tradeMarkImageUrl);
  } catch {
    throw new Error(ERROR_MESSAGES.INVALID_URL_FORMAT);
  }

  if (!cutlineMargin || typeof cutlineMargin !== 'object') {
    throw new Error(ERROR_MESSAGES.INVALID_CUTLINE_MARGIN);
  }

  ['top', 'right', 'bottom', 'left'].forEach((side) => {
    if (typeof cutlineMargin[side] !== 'number' || cutlineMargin[side] < 0) {
      throw new Error(ERROR_MESSAGES.INVALID_CUTLINE_MARGIN);
    }
  });

  if (typeof cutlineRectRound !== 'number' || cutlineRectRound < 0) {
    throw new Error(ERROR_MESSAGES.INVALID_CUTLINE_RECT_ROUND);
  }

  if (typeof ringConnectionRoundPx !== 'number' || ringConnectionRoundPx < 0) {
    throw new Error(ERROR_MESSAGES.INVALID_RING_CONNECTION_ROUND);
  }

  validateBackgroundImageUrls(backgroundImageUrls);
}
